import React, { useEffect, useState } from 'react';
import { Shield, ClipboardList, DollarSign, TrendingUp, AlertTriangle, Database, RefreshCw } from 'lucide-react';
import { getApiBaseUrl } from '../config/api';
import StatsCard from './analytics/StatsCard';
import './UnderwritingCoversIssued.css';

const formatKes = (n) =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    maximumFractionDigits: 0
  }).format(Number(n) || 0);

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (value) => {
  if (!value) return '—';
  const s = String(value).slice(0, 10);
  return s || '—';
};

const coreBadge = (status) => {
  const s = String(status || '').toLowerCase();
  if (['success', 'submitted', 'completed'].includes(s)) {
    return <span className="badge badge-success">Core OK</span>;
  }
  if (['failed', 'error'].includes(s)) {
    return <span className="badge badge-failed">Core failed</span>;
  }
  if (s === 'skipped') {
    return <span className="badge badge-default">Skipped</span>;
  }
  return <span className="badge badge-pending">Core pending</span>;
};

const AdminComprehensiveIssued = ({ onShowNotification }) => {
  const API_BASE_URL = getApiBaseUrl();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedCore, setSelectedCore] = useState('');
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filterVersion, setFilterVersion] = useState(0);
  const [retryingAll, setRetryingAll] = useState(false);
  const [retryingClientId, setRetryingClientId] = useState('');
  const [retryMessage, setRetryMessage] = useState('');
  const [trailClientId, setTrailClientId] = useState('');
  const [trailEvents, setTrailEvents] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
    'Content-Type': 'application/json'
  });

  const needsCoreRetry = (row) => {
    const s = String(row?.core_submission_status || '').toLowerCase();
    if (['success', 'submitted', 'completed'].includes(s) && row?.core_reference_number) return false;
    if (['success', 'submitted', 'completed'].includes(s) && !row?.core_reference_number) return true;
    return true;
  };

  const loadTrail = async (clientId) => {
    if (!clientId) return;
    if (trailClientId === clientId) {
      setTrailClientId('');
      setTrailEvents([]);
      return;
    }
    setTrailLoading(true);
    setTrailClientId(clientId);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/comprehensive/issued/events?clientId=${encodeURIComponent(clientId)}&limit=80`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Trail failed (${res.status})`);
      setTrailEvents(Array.isArray(data.events) ? data.events : []);
    } catch (e) {
      setTrailEvents([]);
      if (onShowNotification) onShowNotification(e.message || 'Failed to load issuance trail', 'error');
    } finally {
      setTrailLoading(false);
    }
  };

  const runCoreRetry = async ({ clientId = null, limit = 25 } = {}) => {
    const isSingle = !!clientId;
    if (isSingle) setRetryingClientId(clientId);
    else setRetryingAll(true);
    setRetryMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/comprehensive/issued/retry-core`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ clientId: clientId || undefined, limit })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Retry failed (${res.status})`);
      }
      const msg =
        data.message ||
        `Submitted ${data.submitted || 0}, failed ${data.failed || 0}, pending ${data.pendingCount ?? '—'}`;
      setRetryMessage(msg);
      if (onShowNotification) onShowNotification(msg, data.failed > 0 && !data.submitted ? 'warning' : 'success');
      await fetchSummary();
      setFilterVersion((v) => v + 1);
    } catch (e) {
      const err = e.message || 'COMP Core retry failed';
      setRetryMessage(err);
      if (onShowNotification) onShowNotification(err, 'error');
    } finally {
      setRetryingAll(false);
      setRetryingClientId('');
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/comprehensive/issued/summary`, {
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Summary failed (${res.status})`);
      }
      setSummary(data.data || null);
    } catch (e) {
      setSummary(null);
      if (onShowNotification) onShowNotification(e.message || 'Failed to load COMP summary', 'warning');
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchRows = async (pageNum = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(selectedAgency && { agencyId: selectedAgency }),
        ...(selectedClass && { vehicleClass: selectedClass }),
        ...(selectedCore && { coreStatus: selectedCore })
      });
      const res = await fetch(`${API_BASE_URL}/api/admin/comprehensive/issued?${params}`, {
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Failed to load (${res.status})`);
      }
      setRows(data.data?.items || []);
      setPagination(data.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (e) {
      setRows([]);
      setError(e.message || 'Failed to load COMP covers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/comprehensive/issued/filters`, {
          headers: authHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setAgencyOptions(data.data?.agencies || []);
          setClassOptions(data.data?.vehicleClasses || []);
        }
      } catch (_) {
        /* filters optional */
      }
    })();
  }, [API_BASE_URL]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 450);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setFilterVersion((v) => v + 1);
  }, [debouncedSearch, selectedAgency, selectedClass, selectedCore, startDate, endDate]);

  useEffect(() => {
    fetchRows(page);
  }, [page, filterVersion]);

  const onClear = () => {
    setSearch('');
    setDebouncedSearch('');
    setStartDate('');
    setEndDate('');
    setSelectedAgency('');
    setSelectedClass('');
    setSelectedCore('');
    setPage(1);
    setFilterVersion((v) => v + 1);
    fetchSummary();
  };

  const stats = [
    {
      title: 'COMP issued',
      value: String(summary?.totalIssued ?? '—'),
      subtitle: 'Live certificates',
      icon: Shield,
      variant: 'primary'
    },
    {
      title: 'This month',
      value: String(summary?.issuedThisMonth ?? '—'),
      subtitle: `${summary?.issuedToday ?? 0} today`,
      icon: ClipboardList,
      variant: 'default'
    },
    {
      title: 'Premium (paid)',
      value: summary ? formatKes(summary.totalPremium) : '—',
      subtitle: summary ? `${formatKes(summary.premiumThisMonth)} this month` : '—',
      icon: DollarSign,
      variant: 'default'
    },
    {
      title: 'Core OK / failed',
      value: summary ? `${summary.coreOk} / ${summary.coreFailed}` : '—',
      subtitle: summary ? `${summary.corePending} pending` : '—',
      icon: Database,
      variant: summary?.coreFailed > 0 ? 'primary' : 'default'
    },
    {
      title: 'Extensions due',
      value: String(summary?.continuationsActionNeeded ?? '—'),
      subtitle: '≤16 days / recoverable',
      icon: summary?.continuationsActionNeeded > 0 ? AlertTriangle : TrendingUp,
      variant: summary?.continuationsActionNeeded > 0 ? 'primary' : 'default'
    }
  ];

  const pendingCore =
    summary != null
      ? Number(summary.coreFailed || 0) + Number(summary.corePending || 0)
      : null;

  return (
    <div className="underwriting-covers-issued ab-page">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '12px 14px',
          borderRadius: 8,
          background: '#eff6ff',
          border: '1px solid #bfdbfe'
        }}
      >
        <div style={{ fontSize: 14, color: '#1e3a8a', lineHeight: 1.45, maxWidth: '42rem' }}>
          <strong>Track COMP certificates</strong> — premium, Core posting, extensions due.
          {pendingCore != null && (
            <span style={{ display: 'block', marginTop: 4, color: '#1d4ed8' }}>
              {pendingCore} cover{pendingCore === 1 ? '' : 's'} still need Core (failed / pending / skipped).
              Hourly job also retries automatically.
            </span>
          )}
        </div>
        <button
          type="button"
          className="btn-filter"
          disabled={retryingAll || !!retryingClientId}
          onClick={() => runCoreRetry({ limit: 50 })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#1a2f5c',
            color: '#f5c518',
            border: 'none',
            padding: '0.65rem 1rem',
            borderRadius: 8,
            fontWeight: 700,
            cursor: retryingAll ? 'wait' : 'pointer'
          }}
          title="Retry Core for COMP covers not yet successfully posted"
        >
          <RefreshCw size={16} className={retryingAll ? 'spin' : undefined} />
          {retryingAll ? 'Retrying Core…' : 'Retry pending Core'}
        </button>
      </div>
      {retryMessage && (
        <div
          className={retryMessage.toLowerCase().includes('fail') && !/submitted\s*[1-9]/i.test(retryMessage) ? 'error-alert' : 'success-alert'}
          style={{ marginBottom: '0.75rem' }}
        >
          {retryMessage}
        </div>
      )}

      <div className="ab-stats-grid ab-stats-grid-5" style={{ marginBottom: '1.25rem' }}>
        {summaryLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton-card" style={{ minHeight: 96 }} />
            ))
          : stats.map((card) => (
              <StatsCard
                key={card.title}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                icon={card.icon}
                variant={card.variant}
              />
            ))}
      </div>

      {!summaryLoading && summary?.byVehicleClass?.length > 0 && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '12px 14px',
            borderRadius: 8,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            fontSize: 13,
            color: '#334155'
          }}
        >
          <strong style={{ marginRight: 8 }}>By class:</strong>
          {summary.byVehicleClass.map((row) => (
            <span key={row.vehicle_class} style={{ marginRight: 14 }}>
              {row.vehicle_class}: <strong>{row.count}</strong>
              <span style={{ color: '#64748b' }}> ({formatKes(row.premium)})</span>
            </span>
          ))}
        </div>
      )}

      <div className="filters-row">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search policy, plate, client, cert, agency…"
          className="filter-input"
        />
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-input" />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-input" />
        <select
          value={selectedAgency}
          onChange={(e) => setSelectedAgency(e.target.value)}
          className="filter-input"
        >
          <option value="">All agencies</option>
          {agencyOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          className="filter-input"
        >
          <option value="">All classes</option>
          {classOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={selectedCore}
          onChange={(e) => setSelectedCore(e.target.value)}
          className="filter-input"
        >
          <option value="">All Core statuses</option>
          <option value="success">Core OK</option>
          <option value="failed">Core failed</option>
          <option value="pending">Core pending</option>
        </select>
        <button type="button" className="btn-filter" onClick={() => { setFilterVersion((v) => v + 1); fetchSummary(); }}>
          Refresh
        </button>
        <button type="button" className="btn-filter" onClick={onClear}>
          Clear
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner-large" />
          <p>Loading Comprehensive covers…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🛡️</div>
          <h3>No Comprehensive covers issued yet</h3>
          <p>Paid COMP certificates will appear here for progress tracking.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="covers-table">
              <thead>
                <tr>
                  <th>Issued</th>
                  <th>Policy No.</th>
                  <th>Plate</th>
                  <th>Client</th>
                  <th>Agency</th>
                  <th>Class</th>
                  <th>SI</th>
                  <th>Paid</th>
                  <th>Cert</th>
                  <th>Core</th>
                  <th>Cover ends</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.client_id}>
                    <td>{formatDateTime(row.issued_at)}</td>
                    <td>
                      <code style={{ fontSize: 12 }}>{row.policy_number || '—'}</code>
                    </td>
                    <td>{row.registration_number || '—'}</td>
                    <td>{row.insured_name || '—'}</td>
                    <td>{row.agency_name || '—'}</td>
                    <td>{row.vehicle_class || '—'}</td>
                    <td>{row.sum_insured != null ? formatKes(row.sum_insured) : '—'}</td>
                    <td>{formatKes(row.paid_amount != null ? row.paid_amount : row.premium_amount)}</td>
                    <td>
                      <span title={row.dmvic_certificate_number || ''}>
                        {(row.dmvic_certificate_number || '—').slice(0, 14)}
                        {(row.dmvic_certificate_number || '').length > 14 ? '…' : ''}
                      </span>
                    </td>
                    <td>
                      <span title={row.core_submission_error || row.core_submission_status || ''}>
                        {coreBadge(row.core_submission_status)}
                      </span>
                    </td>
                    <td>{formatDate(row.end_date)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn-filter"
                          onClick={() => loadTrail(row.client_id)}
                          style={{ fontSize: 12, padding: '0.35rem 0.6rem' }}
                          title="View reserve → pay → DMVIC → Core trail"
                        >
                          {trailLoading && trailClientId === row.client_id
                            ? '…'
                            : trailClientId === row.client_id
                              ? 'Hide trail'
                              : 'Trail'}
                        </button>
                        {needsCoreRetry(row) ? (
                          <button
                            type="button"
                            className="btn-filter"
                            disabled={retryingAll || retryingClientId === row.client_id}
                            onClick={() => runCoreRetry({ clientId: row.client_id, limit: 1 })}
                            style={{ fontSize: 12, padding: '0.35rem 0.6rem' }}
                            title="Retry Core for this COMP cover"
                          >
                            {retryingClientId === row.client_id ? '…' : 'Retry Core'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {trailClientId && (
        <div
          style={{
            marginTop: '1rem',
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#f8fafc'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
            <strong style={{ fontSize: 14 }}>
              Issuance trail — <code>{trailClientId}</code>
            </strong>
            <button type="button" className="btn-filter" onClick={() => { setTrailClientId(''); setTrailEvents([]); }}>
              Close
            </button>
          </div>
          {trailLoading ? (
            <p style={{ margin: 0, color: '#64748b' }}>Loading events…</p>
          ) : trailEvents.length === 0 ? (
            <p style={{ margin: 0, color: '#64748b' }}>
              No events yet for this cover (new logging applies to covers issued after this deploy).
            </p>
          ) : (
            <div className="table-container">
              <table className="covers-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th>Event</th>
                    <th>Error / detail</th>
                  </tr>
                </thead>
                <tbody>
                  {trailEvents.map((ev) => (
                    <tr key={ev.id}>
                      <td>{formatDateTime(ev.created_at)}</td>
                      <td>{ev.stage}</td>
                      <td>{ev.status}</td>
                      <td>
                        <code style={{ fontSize: 11 }}>{ev.event}</code>
                      </td>
                      <td style={{ maxWidth: 360, fontSize: 12 }}>
                        {ev.error_message ||
                          (ev.detail ? JSON.stringify(ev.detail).slice(0, 180) : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
            Also written to <code>logs/comp-issuance.log</code> (grep <code>[COMP_ISSUE]</code>).
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminComprehensiveIssued;
